import { useEffect, useState } from 'react'
import { SidebarMenu } from 'components/SidebarMenu/SidebarMenu'
import { Navbar } from 'components/Navbar'
import { ArcTopBar } from 'components/ArcTopBar'
import { useTranslation } from 'react-i18next'
import { ContainerGlobal, ContainerNotification } from './styled'
import { useLocation } from 'react-router-dom'
import { useSearchParams } from 'react-router-dom'
import { getSidebarUserId, hideZenhubButton } from './util'
import { useNotification } from 'context/useNotificationContext'
import { Notification } from '@mantine/core'
import { IconX, IconCheck } from '@tabler/icons-react'
import { useCurrentUser } from 'context'
import { useIsInsideArcShell } from 'components/ArcShell/ArcShellContext'
import { useFeatureToggle, useFeatureToggleLoading } from 'hooks'
import { isArcBrandingActive } from 'utils/documentTitle'

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { pathname } = location
  const isApp = searchParams.get('app') === 'true'
  const { notification, text, handleNotification, kind } = useNotification()
  const isArcTopBarEnabled = useFeatureToggle('arc-mosaico-project') as boolean
  // Under Arc the chrome normally lives in the persistent ArcShellLayout
  // (router level); Layout only renders it as a fallback outside the shell
  const insideArcShell = useIsInsideArcShell()
  const rendersOwnChrome = !(isArcTopBarEnabled && insideArcShell)
  // While the per-user flags resolve, an arc-branded user (cached signal) must
  // not flash the legacy chrome — notably the yellow mobile navbar band.
  // Flag-off users have no arc-branding signal, so their chrome is untouched.
  const isFeaturesLoading = useFeatureToggleLoading()
  const holdLegacyChrome = isFeaturesLoading && !isArcTopBarEnabled && isArcBrandingActive()
  // Arc sidebar state lives here so the ArcTopBar burger and the sidebar share it
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)

  useEffect(() => {
    if (isApp) {
      hideZenhubButton()
    }
  }, [])

  const userIdStr = getSidebarUserId(currentUser)
  return (
    <>
      {rendersOwnChrome &&
        !holdLegacyChrome &&
        (isArcTopBarEnabled ? (
          <ArcTopBar
            hidden={isApp}
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={() => setSidebarCollapsed(current => !current)}
          />
        ) : (
          <Navbar />
        ))}
      {!isApp && rendersOwnChrome && !holdLegacyChrome && (
        <SidebarMenu
          profile={currentUser && currentUser.profile_used}
          pathname={pathname}
          userID={userIdStr}
          collapsed={isArcTopBarEnabled ? sidebarCollapsed : undefined}
        />
      )}
      <ContainerNotification>
        {notification && (
          <Notification
            onClose={() => handleNotification(false, '')}
            icon={kind === 'success' ? <IconCheck size='1.1rem' /> : <IconX size='1.1rem' />}
            color={kind === 'success' ? 'green' : 'red'}
            closeButtonProps={{ title: t('student_creation_screen.accessibility') }}
            styles={() => ({
              root: {
                maxWidth: '570px',
                padding: '16px',
              },
            })}
          >
            {text}
          </Notification>
        )}
      </ContainerNotification>

      <ContainerGlobal isApp={isApp} $arcTopBar={isArcTopBarEnabled && !isApp}>
        {children}
      </ContainerGlobal>
    </>
  )
}

const LayoutLogin = ({ children }: { children: React.ReactNode }) => {
  const { notification, text, handleNotification, kind } = useNotification()
  const { t } = useTranslation()

  return (
    <>
      <ContainerNotification>
        {notification && (
          <Notification
            onClose={() => handleNotification(false, '')}
            icon={kind === 'success' ? <IconCheck size='1.1rem' /> : <IconX size='1.1rem' />}
            color={kind === 'success' ? 'green' : 'red'}
            closeButtonProps={{ title: t('student_creation_screen.accessibility') }}
            styles={() => ({
              root: {
                maxWidth: '570px',
                padding: '16px',
              },
            })}
          >
            {text}
          </Notification>
        )}
      </ContainerNotification>

      {children}
    </>
  )
}

Layout.Login = LayoutLogin

export { Layout, LayoutLogin }
